import type { CreateServiceRequest, ScanBound } from "@/types/api";

/**
 * Read a typed scanner value out of a text box.
 *
 * A scan's bounds and filter values are compared against stored BSON, so the
 * type matters as much as the characters: `5` and `"5"` order differently, and
 * the engine refuses a `from`/`to` pair of different kinds outright. There is
 * one text box per value, so the type has to be inferred — a number if it
 * reads as one, a boolean for `true`/`false`, otherwise a string, which is
 * what an ObjectId or a date bound is.
 *
 * Whitespace is trimmed, so a quoted-looking value is not mistaken for one:
 * typing `"5"` gives the five-character string, and typing `5` gives the
 * number.
 */
export function parseScanBound(value: string): ScanBound | undefined {
  const trimmed = value.trim();
  if (trimmed === "") return undefined;
  if (trimmed === "true") return true;
  if (trimmed === "false") return false;

  // `Number("")` is 0 and `Number(" ")` is 0, both already excluded above.
  // `Number("0x10")` is 16, which is a stretch but harmless — nothing else
  // would read a hex literal as a string worth matching.
  const asNumber = Number(trimmed);
  if (Number.isFinite(asNumber) && trimmed !== "") return asNumber;

  return trimmed;
}

/** Matches the engine's own default, so the form and the backend agree. */
export const DEFAULT_PAGE_SIZE = 500;

export interface ScannerFormValues {
  targetService: string;
  orderBy: string;
  from: string;
  to: string;
  pageSize: string;
  filters: Array<{ field: string; value: string }>;
}

/**
 * The text the user typed, as the request the engine expects.
 *
 * A pure function rather than a closure inside the hook: this is where a
 * blank box becomes "unset" and a half-typed row becomes nothing, and both
 * are easier to be sure of read on their own than tangled with React state.
 */
export function toScannerPayload(
  values: ScannerFormValues,
): Partial<CreateServiceRequest> {
  const orderBy = values.orderBy.trim();
  const size = Number(values.pageSize.trim());
  const filter = toFilterRecord(values.filters);

  return {
    target_service: values.targetService,
    // Blank leaves the key to the engine: `_id` for a collection, the primary
    // key for a table.
    order_by: orderBy === "" ? undefined : orderBy,
    from: parseScanBound(values.from),
    to: parseScanBound(values.to),
    filter: Object.keys(filter).length > 0 ? filter : undefined,
    page_size: Number.isFinite(size) && size >= 1 ? size : DEFAULT_PAGE_SIZE,
  };
}

/**
 * Filter rows as an equality map, dropping the ones not filled in.
 *
 * A row with no field or no value is not a filter yet — sending `{"": ""}`
 * would turn a stray click on "Add filter" into a scan that matches nothing.
 */
function toFilterRecord(
  rows: Array<{ field: string; value: string }>,
): Record<string, ScanBound> {
  const filter: Record<string, ScanBound> = {};
  for (const row of rows) {
    const field = row.field.trim();
    const value = parseScanBound(row.value);
    if (field !== "" && value !== undefined) filter[field] = value;
  }
  return filter;
}
